import { Sizes } from 'components/forms/NumberSelector';
import { Input } from 'components/forms/fragments/Input';
import { InputWithUnit } from 'components/forms/fragments/InputWithUnit';
import { CardChoice } from 'components/domain/ProfilingForm/CardChoice';
import RadioButtonChoice from 'components/domain/ProfilingForm/RadioButtonChoice';
import React, { useState, useEffect, useCallback } from 'react';
import { HierarchicalAnswer, IconPossibleAnswer, InputAnswer, InputValidationRule, InputWithUnitAnswer, PossibleAnswer, PossibleAnswerWithLabel, Question } from '../../types/interfaces';
import { isBooleanAnswer, isBooleanKeyAnswer, isIconAnswer, isLabelAnswer, isNumberAnswer, isStringAnswer, isHierarchicalAnswer, isInputAnswer, isInputWithUnitAnswer } from '../../types/possibleAnswer';
import { GenericHierarchicalQuestion, useIsHierarchicalQuestion } from './GenericHierarchicalQuestion';
import { Button } from 'components/ui/Button';

// Type pour les réponses avec key boolean
interface BooleanKey {
  key: boolean;
  label: string;
}

interface GenericQuestionProps {
  question: Question;
  currentAnswers: PossibleAnswer[];
  allAnswers?: Record<string, any>; // Toutes les réponses pour les questions hiérarchiques
  onAnswer: (key: string, answer: PossibleAnswer[]) => void;
  isExternal?: boolean; // Si true, la réponse vient de l'extérieur et est en lecture seule
}

export const GenericQuestion: React.FC<GenericQuestionProps> = ({
  question,
  currentAnswers,
  allAnswers = {},
  onAnswer,
  isExternal = false
}) => {
  // Récupérer la valeur par défaut de la config si c'est un input
  const getDefaultValue = (): string => {
    if (currentAnswers[0] !== undefined) return currentAnswers[0] as string;
    const inputAnswer = question.possibleAnswers.find(
      (a): a is InputAnswer | InputWithUnitAnswer =>
        isInputAnswer(a) || isInputWithUnitAnswer(a)
    );
    return inputAnswer?.config?.defaultValue || '';
  };

  // État local pour les inputs (évite l'auto-advance à chaque frappe)
  const [inputValue, setInputValue] = useState<string>(getDefaultValue);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  // Sync avec currentAnswers si elles changent de l'extérieur
  useEffect(() => {
    if (currentAnswers[0] !== undefined) {
      setInputValue(currentAnswers[0] as string);
    }
  }, [currentAnswers]);

  // Fonction de validation générique
  const validateInput = useCallback((value: string, rules?: InputValidationRule[]): string[] => {
    if (!rules || rules.length === 0) return [];

    const errors: string[] = [];

    for (const rule of rules) {
      switch (rule.type) {
        case 'required':
          if (!value || value.trim() === '') {
            errors.push(rule.message);
          }
          break;

        case 'min':
          if (value && Number(value) < Number(rule.value)) {
            errors.push(rule.message);
          }
          break;

        case 'max':
          if (value && Number(value) > Number(rule.value)) {
            errors.push(rule.message);
          }
          break;

        case 'minLength':
          if (value && value.length < Number(rule.value)) {
            errors.push(rule.message);
          }
          break;

        case 'maxLength':
          if (value && value.length > Number(rule.value)) {
            errors.push(rule.message);
          }
          break;

        case 'pattern':
          if (value && !new RegExp(rule.value as string).test(value)) {
            errors.push(rule.message);
          }
          break;

        case 'email':
          if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors.push(rule.message);
          }
          break;

        case 'dateAfterToday': {
          if (value) {
            const inputDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            inputDate.setHours(0, 0, 0, 0);
            if (inputDate <= today) {
              errors.push(rule.message);
            }
          }
          break;
        }

        case 'dateBeforeToday': {
          if (value) {
            const inputDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            inputDate.setHours(0, 0, 0, 0);
            if (inputDate >= today) {
              errors.push(rule.message);
            }
          }
          break;
        }

        case 'dateWithinMonths': {
          if (value) {
            const inputDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const maxDate = new Date(today);
            maxDate.setMonth(maxDate.getMonth() + Number(rule.value));
            inputDate.setHours(0, 0, 0, 0);
            if (inputDate > maxDate) {
              errors.push(rule.message);
            }
          }
          break;
        }

        case 'custom':
          if (rule.validate && !rule.validate(value)) {
            errors.push(rule.message);
          }
          break;
      }
    }

    return errors;
  }, []);

  // Vérifier si cette question est hiérarchique
  const isHierarchical = useIsHierarchicalQuestion(question);

  // Hooks pour la gestion des inputs (doivent être appelés avant tout early return)
  const handleInputChange = useCallback((value: string, rules?: InputValidationRule[]) => {
    setInputValue(value);
    setHasBeenTouched(true);
    const errors = validateInput(value, rules);
    setValidationErrors(errors);
  }, [validateInput]);

  const handleInputValidation = useCallback((rules?: InputValidationRule[]) => {
    const errors = validateInput(inputValue, rules);
    setValidationErrors(errors);
    setHasBeenTouched(true);

    if (errors.length === 0 && inputValue) {
      onAnswer(question.key, [inputValue]);
    }
  }, [inputValue, validateInput, onAnswer, question.key]);

  const isInputValid = useCallback((rules?: InputValidationRule[]): boolean => {
    if (!inputValue) return false;
    const errors = validateInput(inputValue, rules);
    return errors.length === 0;
  }, [inputValue, validateInput]);

  // Si c'est une question hiérarchique, utiliser le composant spécialisé
  if (isHierarchical) {
    const currentAnswer = (currentAnswers as HierarchicalAnswer[])[0]
    return (
      <GenericHierarchicalQuestion
        question={question}
        currentAnswerKey={currentAnswer && currentAnswer.key}
        onAnswer={onAnswer}
      />
    );
  }

  // Fonction pour déterminer si une réponse est sélectionnée
  const isAnswerSelected = (answer: PossibleAnswer): boolean => {
    if (!currentAnswers || currentAnswers.length === 0) return false;

    if (isIconAnswer(answer)) {
      return currentAnswers.some(current =>
        isIconAnswer(current) &&
        current.iconKey === answer.iconKey &&
        current.label === answer.label
      );
    }

    if (isBooleanKeyAnswer(answer)) {
      return currentAnswers.some(current =>
        isBooleanKeyAnswer(current) &&
        current.key === answer.key &&
        current.label === answer.label
      );
    }

    if (isLabelAnswer(answer)) {
      return currentAnswers.some(current =>
        isLabelAnswer(current) &&
        current.key === answer.key &&
        current.label === answer.label
      );
    }

    if (isHierarchicalAnswer(answer)) {
      return currentAnswers.some(current =>
        isHierarchicalAnswer(current) &&
        (current as any).key === (answer as any).key &&
        (current as any).label === (answer as any).label
      );
    }

    if (isBooleanAnswer(answer) || isNumberAnswer(answer) || isStringAnswer(answer)) {
      return currentAnswers.includes(answer);
    }

    return false;
  };

  // Composants de rendu spécialisés
  const renderIconAnswer = (answer: IconPossibleAnswer, index: number) => (
    <div
      key={`${question.key}-${index}`}
      className="flex-grow min-w-0 basis-1/2 sm:basis-1/3 md:basis-1/5 lg:basis-1/5 xl:basis-1/6"
    >
      <CardChoice
        className='w-full h-full'
        iconKey={answer.iconKey}
        label={answer.label}
        isSelected={isAnswerSelected(answer)}
        onClick={() => onAnswer(question.key, [answer])}
      />
    </div>
  );

  const renderBooleanKeyAnswer = (answer: BooleanKey, index: number) => (
    <div key={`${question.key}-${index}`}>
      <RadioButtonChoice
        key={question.key + "true"}
        size={Sizes.lg}
        radio
        text={answer.label}
        selected={isAnswerSelected(answer.key)}
        handleOnClick={() => onAnswer(question.key, [answer.key])}
      />
    </div>
  );

  const renderLabelAnswer = (answer: PossibleAnswerWithLabel, index: number) => (
    <div key={`${question.key}-${index}`}>
      <button
        onClick={() => onAnswer(question.key, [answer])}
        className={`px-4 py-2 rounded ${isAnswerSelected(answer) ? 'bg-primary text-white' : 'bg-gray-200'}`}
      >
        {answer.label}
      </button>
    </div>
  );

  const renderHierarchicalAnswer = (answer: any, index: number) => (
    <div key={`${question.key}-${index}`}>
      <button
        onClick={() => onAnswer(question.key, [answer])}
        className={`px-4 py-2 rounded ${isAnswerSelected(answer) ? 'bg-primary text-white' : 'bg-gray-200'}`}
      >
        {answer.label}
        {answer.hasChildren && <span className="ml-1">▼</span>}
      </button>
    </div>
  );

  const renderBooleanAnswer = (answer: boolean, index: number) => (
    <RadioButtonChoice
      key={question.key + answer}
      size={Sizes.lg}
      radio
      text={answer ? "Oui" : "Non"}
      selected={isAnswerSelected(answer)}
      handleOnClick={() => onAnswer(question.key, [answer])}
    />
  );

  const renderNumberAnswer = (answer: number, index: number) => (
    <div key={`${question.key}-${index}`}>
      <button
        onClick={() => onAnswer(question.key, [answer])}
        className={`px-4 py-2 rounded-xl ${isAnswerSelected(answer) ? 'bg-primary text-white' : 'bg-gray-200'}`}
      >
        {answer}
      </button>
    </div>
  );

  const renderStringAnswer = (answer: string, index: number) => (
    <div key={`${question.key}-${index}`}>
      <button
        onClick={() => onAnswer(question.key, [answer])}
        className={`px-4 py-2 rounded ${isAnswerSelected(answer) ? 'bg-primary text-white' : 'bg-gray-200'}`}
      >
        {answer}
      </button>
    </div>
  );

  const renderInputAnswer = (answer: InputAnswer, index: number) => {
    const rules = answer.validationRules;
    const isValid = isInputValid(rules);
    const showErrors = hasBeenTouched && validationErrors.length > 0;

    return (
      <div key={`${question.key}-${index}`} className="flex flex-col items-center gap-2">
        <div className='flex gap-4'>
          <Input
            value={inputValue}
            type={answer.config.type}
            onChange={(e) => handleInputChange(e.target.value, rules)}
            className={showErrors ? 'border-red-500' : ''}
          />
          <Button
            color="secondary"
            onClick={() => handleInputValidation(rules)}
            disabled={!inputValue || !isValid}
          >
            Valider
          </Button>
        </div>
        {showErrors && validationErrors.map((error, i) => (
          <p key={i} className="text-sm text-red-600 text-center">
            {error}
          </p>
        ))}
      </div>
    );
  };

  const renderInputWithUnitAnswer = (answer: InputWithUnitAnswer, index: number) => {
    const rules = answer.validationRules;
    const isValid = isInputValid(rules);
    const showErrors = hasBeenTouched && validationErrors.length > 0;

    return (
      <div key={`${question.key}-${index}`} className="flex flex-col items-center gap-2">
        <div className='flex gap-4'>
          <InputWithUnit
            value={inputValue}
            type={answer.config.type}
            unit={answer.config.unit}
            min={answer.config.min}
            max={answer.config.max}
            placeholder={answer.config.placeholder}
            onChange={(e) => handleInputChange(e.target.value, rules)}
            className={showErrors ? 'border-red-500' : ''}
          />
          <Button
            color="secondary"
            onClick={() => handleInputValidation(rules)}
            disabled={!inputValue || !isValid}
          >
            Valider
          </Button>
        </div>
        {showErrors && validationErrors.map((error, i) => (
          <p key={i} className="text-sm text-red-600 text-center">
            {error}
          </p>
        ))}
      </div>
    );
  };

  // Fonction de rendu principale
  const renderAnswer = (answer: PossibleAnswer, index: number) => {
    if (isIconAnswer(answer)) return renderIconAnswer(answer, index);
    if (isBooleanKeyAnswer(answer)) return renderBooleanKeyAnswer(answer, index);
    if (isLabelAnswer(answer)) return renderLabelAnswer(answer, index);
    if (isHierarchicalAnswer(answer)) return renderHierarchicalAnswer(answer, index);
    if (isBooleanAnswer(answer)) return renderBooleanAnswer(answer, index);
    if (isNumberAnswer(answer)) return renderNumberAnswer(answer, index);
    if (isStringAnswer(answer)) return renderStringAnswer(answer, index);
    if (isInputAnswer(answer)) return renderInputAnswer(answer, index);
    if (isInputWithUnitAnswer(answer)) return renderInputWithUnitAnswer(answer, index);
    return null;
  };

  const renderAnswers = () => {
    if (question.type === 'boolean') {
      return <>
        {renderBooleanAnswer(true, 1)}
        {renderBooleanAnswer(false, 2)}
      </>
    }
    return question.possibleAnswers.map(renderAnswer)
  }

  // Fonction pour obtenir le label d'une réponse externe
  const getExternalAnswerLabel = (): string => {
    if (!currentAnswers || currentAnswers.length === 0) return '';

    const answer = currentAnswers[0];

    if (typeof answer === 'boolean') {
      return answer ? 'Oui' : 'Non';
    }
    if (typeof answer === 'object' && answer !== null && 'label' in answer) {
      return (answer as PossibleAnswerWithLabel | IconPossibleAnswer).label;
    }
    if (typeof answer === 'string' || typeof answer === 'number') {
      return String(answer);
    }
    return '';
  };

  // Rendu en lecture seule pour les réponses externes
  if (isExternal && currentAnswers.length > 0) {
    const answerLabel = getExternalAnswerLabel();
    const externalAnswer = currentAnswers[0];
    const iconKey = isIconAnswer(externalAnswer) ? externalAnswer.iconKey : undefined;

    return (
      <div className="w-full flex flex-col gap-4 items-center justify-center">
        <div className="flex flex-col gap-1 justify-center items-center">
          <p className="text-sm text-zinc-500">
            Votre sélection
          </p>
        </div>
        <div className="flex items-center gap-3 px-6 py-4 bg-primary/10 border border-primary/30 rounded-xl">
          {iconKey && (
            <div className="flex-shrink-0">
              <CardChoice
                className="pointer-events-none !shadow-none !border-0 !bg-transparent !p-0 scale-75"
                iconKey={iconKey}
                label=""
                isSelected={false}
                onClick={() => {}}
              />
            </div>
          )}
          <p className="text-lg font-semibold text-primary">
            {answerLabel}
          </p>
        </div>
      </div>
    );
  }

  // Rendu standard pour les questions non-hiérarchiques
  return (
    <div className="w-full flex flex-col gap-6 items-center justify-center">
      <div className='flex flex-col gap-2 justify-center items-center'>
        <p className="text-xl text-center text-zinc-800 font-medium">
          {question.label}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 justify-center items-stretch">
        {renderAnswers()}
      </div>
    </div>
  );
};

export default GenericQuestion;
